import React, { useRef, useEffect } from 'react';
import { Box, Flex, Text } from '@mantine/core';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';

export const VERIFICATION_CODE_LENGTH = 6;

const SIZE = {
	SMALL: 'SMALL',
	MEDIUM: 'MEDIUM',
	LARGE: 'LARGE',
} as const;

const mappedStyleWithSize = {
	[SIZE.SMALL]: {
		borderRadius: '4px',
		width: '40px',
		height: '40px',
	},
	[SIZE.MEDIUM]: {
		borderRadius: '6px',
		width: '54px',
		height: '54px',
	},
	[SIZE.LARGE]: {
		borderRadius: '8px',
		width: '61px',
		height: '61px',
	},
};

interface Props {
	numberOfVerificationCodeInputs?: number;
	label?: React.ReactNode;
	errorMessage?: string;
	size?: keyof typeof SIZE;
	value: string;
	onChange: (value: string) => void;
	onSubmit?: () => void;
	disabled?: boolean;
	autoFocus?: boolean;
}

export const VerificationCode = ({
	numberOfVerificationCodeInputs = VERIFICATION_CODE_LENGTH,
	label,
	errorMessage,
	size = SIZE.SMALL,
	value = '',
	onChange,
	onSubmit,
	disabled = false,
	autoFocus = true,
}: Props) => {
	const verificationInputsRef = useRef<HTMLDivElement>(null);

	const getInputElement = (index: number): HTMLInputElement | null => {
		if (verificationInputsRef.current instanceof HTMLDivElement) {
			return verificationInputsRef.current?.querySelector(`input[data-index="${index}"]`);
		}
		return null;
	};

	const convertToArray = () => {
		const arr = value.split('').slice(0, numberOfVerificationCodeInputs);
		while (arr.length < numberOfVerificationCodeInputs) {
			arr.push('');
		}
		return arr;
	};

	useEffect(() => {
		if (autoFocus && !disabled) {
			const firstInput = getInputElement(0);
			if (firstInput) {
				firstInput.focus();
			}
		}
	}, [autoFocus, disabled]);

	const handleVerificationCodeChange = (text: string, index: number) => {
		const digit = text.replace(/[^0-9]/g, '').slice(-1);

		const newValueArray = convertToArray();
		newValueArray[index] = digit;
		const newValue = newValueArray.join('');

		onChange(newValue);

		// Auto-focus next input if digit was entered
		if (digit && index < numberOfVerificationCodeInputs - 1) {
			const nextInput = getInputElement(index + 1);
			if (nextInput) {
				nextInput.focus();
			}
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		const valueArray = convertToArray();

		if (e.key === 'Backspace') {
			const currentValue = valueArray[index];

			if (!currentValue && index > 0) {
				// If current field is empty, move to previous field and clear it
				const newValueArray = convertToArray();
				newValueArray[index - 1] = '';
				onChange(newValueArray.join(''));
				const prevInput = getInputElement(index - 1);
				if (prevInput) {
					prevInput.focus();
				}
			} else if (currentValue) {
				// If current field has value, clear it
				const newValueArray = convertToArray();
				newValueArray[index] = '';
				onChange(newValueArray.join(''));
			}
		}

		if (e.key === 'ArrowLeft' && index > 0) {
			const prevInput = getInputElement(index - 1);
			if (prevInput) {
				prevInput.focus();
			}
		}
		if (e.key === 'ArrowRight' && index < numberOfVerificationCodeInputs - 1) {
			const nextInput = getInputElement(index + 1);
			if (nextInput) {
				nextInput.focus();
			}
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			if (onSubmit && valueArray.every((digit) => digit !== '')) {
				onSubmit();
			}
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		const pastedText = event.clipboardData.getData('text');
		const pastedData = pastedText.replace(/[^0-9]/g, '');
		const newValue = pastedData.slice(0, numberOfVerificationCodeInputs);
		onChange(newValue);

		// Focus the next empty field or the last field
		const nextIndex = Math.min(newValue.length, numberOfVerificationCodeInputs - 1);
		const nextInput = getInputElement(nextIndex);
		if (nextInput) {
			nextInput.focus();
		}
	};

	const valueArray = convertToArray();
	const borderColor = (index: number) => {
		if (errorMessage) {
			return defaultTheme.v3.colors.red[6];
		}
		if (valueArray[index]) {
			return defaultTheme.v3.colors.green[8];
		}
		return defaultTheme.v3.colors.gray[3];
	};

	return (
		<div>
			{label}
			{/* One-time codes - 2FA, change-email and email verification all render this.
			    Masked at the wrapper so every input inside it is masked too. */}
			<Flex
				id="validationEmailCodeForm"
				data-testid={GENERIC_ELEMENT_ID.VERIFICATION_CODE}
				gap="0.5rem"
				onPaste={handlePaste}
				ref={verificationInputsRef}
				className={REPLAY_MASK_CLASS}
				data-sentry-mask
			>
				{Array.from({ length: numberOfVerificationCodeInputs }).map((_, index) => {
					const sizeStyles = mappedStyleWithSize[size];

					return (
						<input
							key={index}
							data-index={index}
							data-testid={`VERIFICATION_CODE_${index}`}
							placeholder="0"
							type="text"
							inputMode="numeric"
							maxLength={1}
							autoComplete="one-time-code"
							value={valueArray[index]}
							disabled={disabled}
							onChange={(event) =>
								handleVerificationCodeChange(event.target.value, index)
							}
							onKeyDown={(e) => handleKeyDown(index, e)}
							className={`
								focus:outline-none focus:ring-0 transition-colors
								${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
								${!errorMessage && !disabled ? 'hover:border-blue-500 focus:border-blue-500' : ''}
							`}
							style={{
								textAlign: 'center',
								fontSize: '2rem',
								fontWeight: '600',
								width: sizeStyles.width,
								height: sizeStyles.height,
								borderRadius: sizeStyles.borderRadius,
								borderWidth: '2px',
								borderStyle: 'solid',
								borderColor: borderColor(index),
							}}
						/>
					);
				})}
			</Flex>
			{errorMessage && (
				<Box mt="0.5rem">
					<Text c="red.6" size="B6Medium">
						{errorMessage}
					</Text>
				</Box>
			)}
		</div>
	);
};
